/**
 * Rules for a self-chosen username.
 *
 * A username is a seller's whole public address (`/s/{username}`), so these
 * rules are stricter than the column: `USERS.username` is VarChar(99) and holds
 * plenty of rows that would fail every check below — empty strings, bare phone
 * numbers, mixed case, `@` from an email prefix. Those are grandfathered. The
 * rules gate what someone may *choose*, not what already exists.
 *
 * ## Length
 *
 * The cap is 30 — the number Instagram uses, and long enough that almost nobody
 * meets it. It deliberately sits well under the column, which is VARCHAR(63):
 * prod's sql_mode carries no STRICT_TRANS_TABLES, so a write that overflows the
 * column is silently truncated rather than rejected. That is not hypothetical —
 * the column was 15 while the schema advertised 99, and live rows still show the
 * damage (`suddenly-950904`, an id cut in half). The gap between 30 and 63 is what
 * lets `mintUsername` append an 8-digit row id to a full-length name and still fit.
 */

import { isReservedUsername } from './reserved-usernames';

export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 30;

/**
 * Lowercase letters, digits, `-` and `_`; must open and close alphanumeric.
 *
 * Digits may lead. `/s/[slug]` used to read any numeric slug as a row id, which
 * made `4x4trucks` ambiguous with somebody else's account; it now resolves the
 * username first and treats a numeric slug only as a legacy redirect, so a name
 * that starts with a number is just a name.
 */
const USERNAME_PATTERN = /^[a-z0-9][a-z0-9_-]*[a-z0-9]$/;

/** Two separators in a row reads as a typo and invites near-identical names. */
const CONSECUTIVE_SEPARATORS = /[-_]{2,}/;

export type UsernameRejection =
  | 'empty'
  | 'too-short'
  | 'too-long'
  | 'invalid-characters'
  | 'must-start-alphanumeric'
  | 'must-end-alphanumeric'
  | 'consecutive-separators'
  | 'all-numeric'
  | 'reserved';

export interface UsernameValidationResult {
  valid: boolean;
  /** Why it was rejected, for tests and logging. Absent when valid. */
  reason?: UsernameRejection;
  /** Sentence shown to the seller. Absent when valid. */
  error?: string;
}

/**
 * Fold a raw input into the form we store and compare.
 *
 * Trimming and lowercasing here rather than rejecting means someone who types
 * "  MyStore " gets "mystore" instead of an error about whitespace they cannot
 * see. Always normalize before validating, comparing or writing.
 */
export function normalizeUsername(input: string): string {
  return input.trim().toLowerCase();
}

/**
 * Check a normalized username against the format and reserved rules.
 *
 * Does not touch the database — uniqueness is a separate check, because it
 * needs a transaction and this does not.
 */
export function validateUsernameFormat(input: string): UsernameValidationResult {
  const username = normalizeUsername(input);

  if (!username) {
    return { valid: false, reason: 'empty', error: 'Enter a username.' };
  }
  if (username.length < USERNAME_MIN_LENGTH) {
    return {
      valid: false,
      reason: 'too-short',
      error: `Usernames need at least ${USERNAME_MIN_LENGTH} characters.`,
    };
  }
  if (username.length > USERNAME_MAX_LENGTH) {
    return {
      valid: false,
      reason: 'too-long',
      error: `Usernames can be at most ${USERNAME_MAX_LENGTH} characters.`,
    };
  }
  if (!/^[a-z0-9]/.test(username)) {
    return {
      valid: false,
      reason: 'must-start-alphanumeric',
      error: 'Usernames must start with a letter or number.',
    };
  }
  // An all-digit name is still reachable as a legacy id URL for the handful of
  // accounts that carry no username (see components/seller-main.tsx), and the
  // column is not unique — thousands of imported rows hold a bare phone number
  // as their username. Letting someone *choose* one invites a collision that
  // 404s both stores. Lift this once those accounts have names of their own.
  if (/^\d+$/.test(username)) {
    return {
      valid: false,
      reason: 'all-numeric',
      error: 'Usernames need at least one letter.',
    };
  }
  if (!/[a-z0-9]$/.test(username)) {
    return {
      valid: false,
      reason: 'must-end-alphanumeric',
      error: 'Usernames must end with a letter or number.',
    };
  }
  if (!USERNAME_PATTERN.test(username)) {
    return {
      valid: false,
      reason: 'invalid-characters',
      error: 'Usernames can use letters, numbers, hyphens and underscores only.',
    };
  }
  if (CONSECUTIVE_SEPARATORS.test(username)) {
    return {
      valid: false,
      reason: 'consecutive-separators',
      error: 'Usernames cannot contain two hyphens or underscores in a row.',
    };
  }
  if (isReservedUsername(username)) {
    return {
      valid: false,
      reason: 'reserved',
      error: 'That username is reserved. Please choose another.',
    };
  }

  return { valid: true };
}

/**
 * Whether this account may change its username right now.
 *
 * One change per account, no exceptions for how the current name came to be —
 * every username starts machine-minted, so "was it chosen?" is not a line worth
 * drawing. The only other way through is staff, who need to be able to fix a
 * seller's mistake; that is what makes the error message below true.
 */
export function canChangeUsername(user: {
  hasChangedUsername: boolean;
  isAdmin?: boolean;
}): { allowed: boolean; error?: string } {
  if (user.isAdmin) return { allowed: true };
  if (!user.hasChangedUsername) return { allowed: true };

  return {
    allowed: false,
    error:
      'You have already changed your username. Contact support if you need it changed again.',
  };
}
